/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Node } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';
import * as React from 'react';

import { IMfrmDeliveryShippingData } from './mfrm-delivery-shipping.data';
import { ILearnMoreLinkData, IMfrmDeliveryShippingProps } from './mfrm-delivery-shipping.props.autogenerated';

export interface IMfrmDeliveryShippingViewProps extends IMfrmDeliveryShippingProps<IMfrmDeliveryShippingData> {
    deliveryShippingContent: React.ReactNode;
    learnMoreLink: ILearnMoreLinkData | undefined;
    tableListContent: React.ReactNode[] | undefined;
}
interface ISelectedDeliveryShippingContent {
    deliveryShippingContent: JSX.Element | undefined;
    learnMoreLink: ILearnMoreLinkData | undefined;
    tableListContent: React.ReactNode[] | undefined;
}

/**
 *
 * MfrmDeliveryShipping component
 * @extends {React.PureComponent<IMfrmDeliveryShippingProps<IMfrmDeliveryShippingData>>}
 */
class MfrmDeliveryShipping extends React.PureComponent<IMfrmDeliveryShippingProps<IMfrmDeliveryShippingData>> {
    public render(): JSX.Element | null {
        const { deliveryShippingContent, learnMoreLink, tableListContent } = this._getSelectedDeliveryShippingValues();
        const viewProps: IMfrmDeliveryShippingViewProps = {
            ...(this.props as IMfrmDeliveryShippingProps<IMfrmDeliveryShippingData>),
            deliveryShippingContent: deliveryShippingContent,
            learnMoreLink: learnMoreLink,
            tableListContent: tableListContent
        };
        return this.props.renderView(viewProps);
    }

    private _getSelectedDeliveryShippingValues = (): ISelectedDeliveryShippingContent => {
        const {
            data: {
                productSpecificationData: { result }
            },
            resources: { defaultContent },
            config: { backendAttribute, deliveryShippingList }
        } = this.props;

        let content;
        let learnMore;
        let tableContent;

        const resultDeliveryShippingData = result
            ? result.find(obj => obj.Name && obj.Name.toLowerCase().trim() === backendAttribute.toLowerCase().trim())
            : undefined;

        const deliveryShippingName = resultDeliveryShippingData?.TextValue;
        // select correct Delivery Shipping Content
        const selectedDeliveryShippingValue =
            (deliveryShippingList &&
                deliveryShippingName &&
                deliveryShippingList.find(
                    deliveryShipping => deliveryShipping.name.toLowerCase().trim() === deliveryShippingName?.toLowerCase().trim()
                )) ||
            undefined;

        if (selectedDeliveryShippingValue) {
            tableContent = selectedDeliveryShippingValue.tableList ? selectedDeliveryShippingValue.tableList.map((cardContent, index) =>{
                if(this.props.config.enableDeliveryShippingInfoRedesign){
                    return((
                        <div key={index} className="mfrm-delivery-shipping-redesign__card">
                            <div className="mfrm-delivery-shipping-redesign__card-top">
                                <RichTextComponent className="mfrm-delivery-shipping-redesign__card-title" text={cardContent.title} />
                                <RichTextComponent className="mfrm-delivery-shipping-redesign__card-description" text={cardContent.description} />
                                <RichTextComponent className="mfrm-delivery-shipping-redesign__card-details" text={cardContent.details} />
                            </div>
                            <div className="mfrm-delivery-shipping-redesign__card-bottom">
                              <div className="mfrm-delivery-shipping-redesign__pricing">
                                <div className='strike'>
                                 <RichTextComponent className="mfrm-delivery-shipping-redesign__strikethrough-price" text={cardContent.listedPrice} />
                                </div>
                                 <RichTextComponent className="mfrm-delivery-shipping-redesign__saleprice" text={cardContent.salePrice} />
                              </div>
                            </div>
                        </div>
                    ));
                 }
                return (
                        <div key={index} className="mfrm-delivery-shipping__card-flex-items">
                            <div className="mfrm-delivery-shipping__card-primary">
                                <RichTextComponent className="mfrm-delivery-shipping__card-title" text={cardContent.title} />
                                <RichTextComponent className="mfrm-delivery-shipping__card-badge" text={cardContent.badge} />
                                <div className="mfrm-delivery-shipping__card-listedPrice-container">
                                    <RichTextComponent className="mfrm-delivery-shipping__card-listedPrice" text={cardContent.listedPrice} />
                                </div>
                                <RichTextComponent className="mfrm-delivery-shipping__card-salePrice" text={cardContent.salePrice} />
                            </div>
                            <hr/>
                            <div className="mfrm-delivery-shipping__card-secondary">
                                <RichTextComponent className="mfrm-delivery-shipping__card-description" text={cardContent.description} />
                                <RichTextComponent className="mfrm-delivery-shipping__card-details" text={cardContent.details} />
                            </div>
                        </div>
                );
            }) : undefined;
            learnMore = selectedDeliveryShippingValue && selectedDeliveryShippingValue.learnMoreLink;
        } else {
            content = <Node className='mfrm-tab-accordion__no-content'>{defaultContent}</Node>;
        }

        return { deliveryShippingContent: content, learnMoreLink: learnMore, tableListContent: tableContent};
    };
}

export default MfrmDeliveryShipping;
